import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Group, Modal, Progress, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import {
	DATE_FORMATS,
	parseBEDateIgnoreTimezone,
	parseUiDateDdMmYyyy,
} from '@dealstack/shared/lib/format/dates/date';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import { EidEasySigningModal } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal';
import { useInvestmentProgrammeDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useInvestmentProgrammeDocument';
import { useApplyDate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useApplyDate';
import type { FormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ApplyDateModal.types';
import { createApplyDateSchema } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ApplyDateModal.utils';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	instanceId: string;
	opened: boolean;
	onClose: () => void;
	onApplied: () => void;
}

export const ApplyDateModal = ({ instanceId, opened, onClose, onApplied }: Props) => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const tMipManage = useTranslationMipManage();

	// Same query key as the parent's document-details read, so this is cache-only.
	const { data: documentData } = useInvestmentProgrammeDocument({
		dealId,
		programmeId: mipId,
		documentWorkflowInstanceId: instanceId,
	});
	const storedEffectiveDateValue = documentData?.payload?.effectiveDate ?? null;
	const storedEffectiveDate = storedEffectiveDateValue
		? parseBEDateIgnoreTimezone(storedEffectiveDateValue)
		: null;

	const { start, reset, status, error, progress } = useApplyDate({
		dealId,
		programmeId: mipId,
		documentWorkflowInstanceId: instanceId,
		onApplied,
	});

	const isSigning = status !== EidEasySignStatus.IDLE;
	const isSigningInProgress =
		status === EidEasySignStatus.PREPARING || status === EidEasySignStatus.COMPLETING;

	const schema = useMemo(() => createApplyDateSchema(tMipManage), [tMipManage]);

	// Prefill only, not RHF's `values`: that would re-assert the stored date on every refetch and
	// undo a correction typed after an aborted attempt.
	const form = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { effectiveDate: storedEffectiveDate },
	});

	// Kept for the error step's "try again", which re-runs the same date rather than reopening
	// the form.
	const submittedDateRef = useRef<string | null>(null);

	const onSubmit = form.handleSubmit(({ effectiveDate }) => {
		if (!effectiveDate) {
			return;
		}
		const beDate = format(effectiveDate, DATE_FORMATS.BE_DATE);
		submittedDateRef.current = beDate;
		void start(beDate);
	});

	const retry = () => {
		const beDate = submittedDateRef.current;
		if (beDate) {
			void start(beDate);
		}
	};

	const closeSigning = () => {
		reset();
		onClose();
	};

	// Side effect, not derivation: the last placement landing is what dismisses the flow.
	useEffect(() => {
		if (status === EidEasySignStatus.DONE) {
			reset();
			onClose();
		}
	}, [status, reset, onClose]);

	if (isSigning) {
		return (
			<EidEasySigningModal
				status={status}
				challengeCode={null}
				error={error}
				isSigningInProgress={isSigningInProgress}
				isModalOpen
				progress={
					progress
						? { phase: 'preparing', current: progress.done, total: progress.total }
						: null
				}
				panelHeader={
					progress && (
						<Stack
							gap={4}
							px="md"
							pt="md"
							data-testid={TEST_ELEMENT_IDS.APPLY_DATE_MODAL_PROGRESS}
							data-done={progress.done}
							data-total={progress.total}
						>
							<Text size="13px" c="gray.7">
								{tMipManage('documents_page.apply_date.modal.progress', {
									done: progress.done,
									total: progress.total,
								})}
							</Text>
							<Progress value={(progress.done / progress.total) * 100} size="sm" />
						</Stack>
					)
				}
				onClose={closeSigning}
				// No method screen: a date needs no credential, so nothing can be selected here.
				onSelectMethod={() => undefined}
				onGoBack={retry}
			/>
		);
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			size="md"
			padding="md"
			radius="md"
			centered
			title={
				<Text size="18px" fw={600}>
					{tMipManage('documents_page.apply_date.modal.title')}
				</Text>
			}
			styles={{
				body: { backgroundColor: '#fff' },
				header: { backgroundColor: '#fff', marginBottom: 0 },
				content: { backgroundColor: '#fff' },
			}}
		>
			<Stack gap="sm">
				<Text size="14px" c="gray.7">
					{tMipManage('documents_page.apply_date.modal.description')}
				</Text>

				<Controller
					control={form.control}
					name="effectiveDate"
					render={({ field, fieldState }) => (
						<DateInput
							label={tMipManage('documents_page.apply_date.modal.date_label')}
							placeholder={tMipManage(
								'documents_page.apply_date.modal.date_placeholder'
							)}
							value={field.value}
							onChange={field.onChange}
							onBlur={field.onBlur}
							error={fieldState.error?.message}
							// Deliberately unbounded in both directions: an effective date can
							// precede signing (a deal that closed weeks ago) or follow it (a
							// future closing), and the revision's own timestamp stays truthful
							// either way. Not locked on the stored date either — the server
							// accepts a correction until a revision lands, and rejects a
							// mismatch after.
							valueFormat="DD/MM/YYYY"
							// Without this Mantine parses typed input as US MM/DD/YYYY, so a
							// day past the 12th is rejected and an earlier one lands on the
							// wrong month — in a date that gets stamped irreversibly.
							dateParser={parseUiDateDdMmYyyy}
							popoverProps={{ withinPortal: true, zIndex: 1000 }}
							data-testid={TEST_ELEMENT_IDS.APPLY_DATE_MODAL_DATE_INPUT}
						/>
					)}
				/>

				<Group justify="right" gap="xs">
					<ButtonV2 size="s" buttonTheme="light" variant="tertiary" onClick={onClose}>
						{tMipManage('documents_page.apply_date.modal.cancel')}
					</ButtonV2>
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="primary"
						onClick={() => void onSubmit()}
						data-testid={TEST_ELEMENT_IDS.APPLY_DATE_MODAL_SUBMIT_BUTTON}
					>
						{tMipManage('documents_page.apply_date.modal.submit')}
					</ButtonV2>
				</Group>
			</Stack>
		</Modal>
	);
};
